import format from 'date-fns/format';
import { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import formatCurrency from '../lib/formatCurrency';
import Modal, { ModalActions, ModalTitle } from './Modal';

const initialState = {
  data: null,
  key: null,
  open: false,
};

const pkgName = process.env.REACT_APP_PACKAGE_NAME;

const EVENTS = {
  open: `${pkgName}:AppDialog:open`,
};

export default function AppDialog() {
  const history = useHistory();
  const location = useLocation();
  const [state, setState] = useState({
    ...initialState,
  });

  useEffect(() => {
    if (location.hash === '#subscribe-success') {
      setState({
        open: true,
        key: 'subscriptionConfirmation',
      });
      history.replace({
        ...location,
        hash: '',
      });
    }
  }, [history, location]);

  useEffect(() => {
    document.addEventListener(EVENTS.open, event => {
      const { key, ...data } = event.detail;
      if (process.env.NODE_ENV === 'development') {
        console.log(`${EVENTS.open} listener fired`);
        console.table(event.detail);
      }
      setState({
        data,
        key,
        open: true,
      });
    });
  }, []);

  const onClose = () =>
    setState({
      ...initialState,
    });

  const modalProps = {
    data: state.data,
    onClose,
  };

  return (
    <Modal onClose={onClose} open={state.open}>
      {state.key === 'bidSuccessLeading' ? (
        <LeadingBidder {...modalProps} />
      ) : state.key === 'outbid' ? (
        <Outbid {...modalProps} />
      ) : state.key === 'bidSuccessOutbid' ? (
        <OutbidProxy {...modalProps} />
      ) : state.key === 'subscriptionConfirmation' ? (
        <SubscriptionConfirmation {...modalProps} />
      ) : state.key === 'subscriptionDisclosure' ? (
        <SubscriptionDisclosure {...modalProps} />
      ) : state.key === 'invalidQuery' ? (
        <InvalidQuery {...modalProps} />
      ) : null}
    </Modal>
  );
}

AppDialog.events = EVENTS;

const LeadingBidder = ({ data: { bid, sale }, onClose }) => {
  const bidAmount = formatCurrency(bid.maxAmount);
  return (
    <>
      <ModalTitle>
        Congratulations, you’re the leading bidder on{' '}
        <b className="tw-text-black">{sale.product.name}</b>!
      </ModalTitle>
      <p id="modal-description">
        We’ve successfully received your bid for {bidAmount} on{' '}
        {sale.product.name}.
      </p>
      {bid.maxAmount > sale.minBid && (
        <>
          <h3 className="tw-text-xl tw-font-bold tw-mt-4 tw-mb-2">
            What happens next?
          </h3>
          <p>
            You’re currently the leading bidder on {sale.product.name}. Because
            your bid is above the domain’s current minimum bid of{' '}
            {formatCurrency(sale.minBid)}, we will automatically bid for you up
            until your bid amount of {bidAmount}. If someone outbids you, we’ll
            send you an email immediately in case you want to make another bid.
          </p>
        </>
      )}
      {bid.isSnipe && <BidSnipeText sale={sale} />}
      <ModalActions>
        <button
          autoFocus
          className="tw-px-4 gb-btn gb-btn--lg gb-btn--primary"
          onClick={onClose}>
          Okay
        </button>
      </ModalActions>
    </>
  );
};

const Outbid = ({ data: { sale }, onClose }) => {
  return (
    <>
      <ModalTitle>
        You’ve just been outbid on{' '}
        <b className="tw-text-black">{sale.product.name}</b>!
      </ModalTitle>
      <p className="tw-mb-2" id="modal-description">
        Another bidder has just become the leading bidder on {sale.product.name}{' '}
        by placing a bid higher than your previous maximum bid. Try bidding
        again with a higher amount if you want to win this domain.
      </p>
      <ModalActions>
        <button className="gb-btn gb-btn--lg tw-mr-2" onClick={onClose}>
          Cancel
        </button>
        <Link
          autoFocus
          className="gb-btn gb-btn--lg gb-btn--primary"
          to={`/${sale.product.name}/`}
          onClick={onClose}>
          Bid Again
        </Link>
      </ModalActions>
    </>
  );
};

const OutbidProxy = ({ data: { bid, sale }, onClose }) => {
  const bidAmount = formatCurrency(bid.maxAmount);
  return (
    <>
      <ModalTitle>You’ve been outbid by another bidder</ModalTitle>
      <p id="modal-description">
        We’ve successfully received your bid for {bidAmount} on{' '}
        {sale.product.name}. However you’ve been outbid by another bidder.
      </p>
      <h3 className="tw-text-xl tw-font-bold tw-text-gray-700 tw-mt-4 tw-mb-2">
        How did this happen?
      </h3>
      <p>
        Another user has set their maximum bid on {sale.product.name} to an
        amount higher than or equal to your bid of {bidAmount}, so we’ve
        automatically bid again on their behalf. If you’d like to become the
        leading bidder, try bidding again with a higher amount.
      </p>
      {bid.isSnipe && <BidSnipeText sale={sale} />}
      <ModalActions>
        <button className="gb-btn gb-btn--lg tw-mr-2" onClick={onClose}>
          Cancel
        </button>
        <Link
          autoFocus
          className="gb-btn gb-btn--lg gb-btn--primary"
          to={`/${sale.product.name}/`}
          onClick={onClose}>
          Bid Again
        </Link>
      </ModalActions>
    </>
  );
};

const SubscriptionConfirmation = ({ onClose }) => {
  return (
    <>
      <ModalTitle>You’re now subscribed</ModalTitle>
      <p id="modal-description">
        We processed your subscription successfully. You may now place bids!
      </p>
      <ModalActions>
        <button
          autoFocus
          className="tw-px-4 gb-btn gb-btn--lg gb-btn--primary"
          onClick={onClose}>
          Okay
        </button>
      </ModalActions>
    </>
  );
};

const SubscriptionDisclosure = ({ data: { returnTo }, onClose }) => {
  const history = useHistory();
  return (
    <>
      <ModalTitle>Subscribe to Namecheap Aftermarket</ModalTitle>
      <p id="modal-description">
        Before you can place a bid, you must first subscribe to the Namecheap
        Aftermarket. We require this as an effort towards preventing bidder
        fraud and protecting the integrity of domain auctions.
      </p>
      <ModalActions>
        <button
          autoFocus
          className="gb-btn gb-btn--lg tw-mr-2"
          onClick={onClose}>
          Cancel
        </button>
        <button
          autoFocus
          className="gb-btn gb-btn--lg gb-btn--primary"
          onClick={() => {
            onClose();
            history.push(`/subscribe/?returnTo=${returnTo}`);
          }}>
          Subscribe
        </button>
      </ModalActions>
    </>
  );
};

const InvalidQuery = ({ onClose }) => {
  return (
    <>
      <ModalTitle>There was a problem</ModalTitle>
      <p id="modal-description">
        A server error occurred. Please press 'Reload' in your browser.
      </p>
      <ModalActions>
        <button
          autoFocus
          className="gb-btn gb-btn--lg gb-btn--primary tw-px-8"
          onClick={onClose}>
          OK
        </button>
      </ModalActions>
    </>
  );
};

const BidSnipeText = ({ sale }) => (
  <>
    <h3 className="tw-text-xl tw-font-bold tw-mt-4 tw-mb-2">
      The auction end time was extended.
    </h3>
    <p>
      To give other bidders a chance to place a bid before the auction ends, we
      have extended the auction end time
      {sale.endDate && (
        <>
          {' '}
          to <strong>{format(new Date(sale.endDate), 'pp')}</strong>
        </>
      )}
      .
    </p>
  </>
);
